import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ArrowRight, Wrench, Laptop, Dumbbell, Tent, ChefHat, TreePine, PartyPopper, Baby, Sofa, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ItemCard from '../components/ItemCard';
import { getItems, getCategories } from '../services/api';

const categoryIcons = {
  'Tools': Wrench,
  'Electronics': Laptop,
  'Sports': Dumbbell,
  'Outdoors': Tent,
  'Kitchen': ChefHat,
  'Garden': TreePine,
  'Party': PartyPopper,
  'Baby & Kids': Baby,
  'Furniture': Sofa,
  'Other': MoreHorizontal
};

const LandingPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredItems, setFeaturedItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsData, categoriesData] = await Promise.all([
          getItems({ available_only: true }),
          getCategories()
        ]);
        setFeaturedItems(itemsData.slice(0, 8));
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/browse?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>NeighborBorrow — Borrow and Lend Items in Your Neighborhood</title>
        <meta name="description" content="Rent tools, electronics, books, and more from trusted neighbors. Secure payments, insurance protection, and ID-verified users. Join your neighborhood sharing community today." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/1084374/pexels-photo-1084374.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
            alt="Neighborhood"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/60 via-[#FDFBF7]/80 to-[#FDFBF7]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-[#8C9A76]/20 text-[#596652] mb-6 animate-fade-in">
              Community-powered sharing
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-[#2C3329] mb-6 animate-fade-in stagger-1">
              Borrow from your
              <span className="text-[#D97757]"> neighbors</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-[#596652] mb-8 animate-fade-in stagger-2">
              Why buy when you can borrow? Connect with neighbors to rent tools, equipment, and more at a fraction of the cost.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto animate-fade-in stagger-3">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#596652]" />
                <Input
                  type="text"
                  placeholder="What do you need to borrow?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-32 py-4 text-lg bg-white border-[#EBE8DF] rounded-full shadow-soft focus:ring-2 focus:ring-[#8C9A76]/50"
                  data-testid="hero-search-input"
                />
                <Button 
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#D97757] text-white rounded-full hover:bg-[#C56547] px-6"
                  data-testid="hero-search-button"
                >
                  Search
                </Button>
              </div>
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-12 animate-fade-in stagger-4">
              <div className="text-center">
                <p className="text-3xl font-heading font-semibold text-[#2C3329]">500+</p>
                <p className="text-[#596652] text-sm">Items Available</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-heading font-semibold text-[#2C3329]">50+</p>
                <p className="text-[#596652] text-sm">Neighborhoods</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-heading font-semibold text-[#2C3329]">1000+</p>
                <p className="text-[#596652] text-sm">Happy Neighbors</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#2C3329] mb-3">
              Browse by Category
            </h2>
            <p className="text-[#596652]">Find exactly what you need</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {categories.map((category) => {
              const Icon = categoryIcons[category] || MoreHorizontal;
              return (
                <Link
                  key={category}
                  to={`/browse?category=${encodeURIComponent(category)}`}
                  className="flex flex-col items-center p-6 bg-[#FDFBF7] rounded-2xl border border-[#EBE8DF] hover:border-[#8C9A76] hover:shadow-soft transition-all group"
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-12 h-12 bg-[#8C9A76]/15 rounded-full flex items-center justify-center mb-3 group-hover:bg-[#8C9A76]/25 transition-colors">
                    <Icon className="w-6 h-6 text-[#596652]" />
                  </div>
                  <span className="text-[#2C3329] font-medium text-sm text-center">{category}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Items Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#2C3329] mb-2">
                Featured Items
              </h2>
              <p className="text-[#596652]">Popular items in your community</p>
            </div>
            <Link 
              to="/browse"
              className="hidden sm:flex items-center space-x-2 text-[#D97757] hover:text-[#C56547] font-medium transition-colors"
              data-testid="view-all-items-link"
            >
              <span>View all</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white rounded-2xl border border-[#EBE8DF] overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-[#EBE8DF]"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-[#EBE8DF] rounded w-1/4"></div>
                    <div className="h-5 bg-[#EBE8DF] rounded w-3/4"></div>
                    <div className="h-4 bg-[#EBE8DF] rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredItems.map((item) => (
                <ItemCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-[#EBE8DF]">
              <p className="text-[#596652] mb-4">No items listed yet. Be the first!</p>
              <Link to="/list-item">
                <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]" data-testid="first-list-item-button">
                  List an Item
                </Button>
              </Link>
            </div>
          )}

          <div className="sm:hidden mt-6 text-center">
            <Link 
              to="/browse"
              className="inline-flex items-center space-x-2 text-[#D97757] hover:text-[#C56547] font-medium"
              data-testid="mobile-view-all-link"
            >
              <span>View all items</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#2C3329] mb-3">
              How It Works
            </h2>
            <p className="text-[#596652]">Simple, secure, and community-driven</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#D97757]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-heading font-bold text-[#D97757]">1</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-2">Find What You Need</h3>
              <p className="text-[#596652]">Browse items available in your neighborhood or search for something specific.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#8C9A76]/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-heading font-bold text-[#8C9A76]">2</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-2">Request & Pay</h3>
              <p className="text-[#596652]">Send a rental request, chat with the owner, and pay securely through Stripe.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#D97757]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-heading font-bold text-[#D97757]">3</span>
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#2C3329] mb-2">Pick Up & Return</h3>
              <p className="text-[#596652]">Coordinate with your neighbor, use the item, and return it when you're done.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#2C3329] rounded-3xl p-8 sm:p-12 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#FDFBF7] mb-4">
              Have items collecting dust?
            </h2>
            <p className="text-[#FDFBF7]/70 mb-8 max-w-xl mx-auto">
              Turn your unused items into extra income. List them for rent and help your neighbors while earning money.
            </p>
            <Link to="/list-item" data-testid="cta-list-item-link">
              <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547] px-8 py-3 text-lg">
                Start Listing Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
